export function isValidHttpUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === "http:" || urlObj.protocol === "https:";
  } catch {
    return false;
  }
}

export function normalizeUrlOriginPath(url: string): string {
  try {
    const u = new URL(url);
    return u.origin + u.pathname;
  } catch {
    return url;
  }
}
/**
 * Scores how well an ACL URL covers a referrer URL. Returns -1 when they
 * don't match. Otherwise the score is the length of the ACL's path: a
 * higher score means a more specific match. A bare-origin ACL (no path)
 * scores 0 and matches any referrer on the same protocol/host/port, while a
 * path-scoped ACL only matches when the referrer falls under that path.
 * Query and fragment are ignored.
 */
export function referrerMatchScore(referrer: string, aclUrl: string): number {
  try {
    const r = new URL(referrer);
    const a = new URL(aclUrl);
    if (r.protocol !== a.protocol || r.hostname !== a.hostname || r.port !== a.port) {
      return -1;
    }
    const aclPath = a.pathname.replace(/\/+$/, "");
    if (aclPath && !(r.pathname === aclPath || r.pathname.startsWith(aclPath + "/"))) {
      return -1;
    }
    return aclPath.length;
  } catch {
    return -1;
  }
}

export interface MatchableAcl {
  ACLID?: string;
  URL: string;
  Status: number;
}

export interface SelectReferrerMatchOptions<T extends MatchableAcl> {
  /**
   * Hard filter: only ACLs for which this returns true are considered. Use it
   * for a caller's must-hold precondition (e.g. the grant is live, or the knoc
   * opts into referrer redirects). Defaults to accepting every ACL.
   */
  eligible?: (acl: T) => boolean;
  /**
   * Soft preference used only to break ties between equally specific matches:
   * an ACL for which this returns true wins over one for which it doesn't.
   * Defaults to no preference.
   */
  preferLive?: (acl: T) => boolean;
}

/**
 * Deterministically selects the best ACL for a referrer. Among eligible
 * candidates that match (see referrerMatchScore), the winner is chosen by:
 *   1. highest score (most specific URL path),
 *   2. then preferLive (a live grant over a not-yet-live one),
 *   3. then the lexicographically smallest ACLID for a stable result that
 *      doesn't flap as grant statuses change across polls.
 * Returns null when nothing matches.
 */
export function selectReferrerMatch<T extends MatchableAcl>(
  referrer: string,
  acls: ReadonlyArray<T>,
  opts: SelectReferrerMatchOptions<T> = {},
): T | null {
  const eligible = opts.eligible ?? (() => true);
  const preferLive = opts.preferLive ?? (() => false);
  let best: T | null = null;
  let bestScore = -1;
  let topScoreCount = 0;
  for (const acl of acls) {
    if (!eligible(acl)) {
      continue;
    }
    const score = referrerMatchScore(referrer, acl.URL);
    if (score < 0) {
      continue;
    }
    if (score > bestScore) {
      topScoreCount = 1;
    } else if (score === bestScore) {
      topScoreCount += 1;
    }
    if (best === null || isBetterMatch(acl, score, best, bestScore, preferLive)) {
      best = acl;
      bestScore = score;
    }
  }
  if (topScoreCount > 1) {
    console.debug(`[referrer-match] ${topScoreCount} knocs tie for ${referrer}; selected ACLID=${best?.ACLID ?? "?"}`);
  }
  return best;
}

function isBetterMatch<T extends MatchableAcl>(
  candidate: T,
  candidateScore: number,
  current: T,
  currentScore: number,
  preferLive: (acl: T) => boolean,
): boolean {
  if (candidateScore !== currentScore) {
    return candidateScore > currentScore;
  }
  const candidateLive = preferLive(candidate);
  const currentLive = preferLive(current);
  if (candidateLive !== currentLive) {
    return candidateLive;
  }
  return (candidate.ACLID ?? "") < (current.ACLID ?? "");
}
